import React, { useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';

import EmptyLoadingState from 'components/empty-loading-state';
import { TwoColumnSidebar } from 'components/layouts/two-column';
import { useStickySidebar } from 'hooks/use-sticky-sidebar';
import useUI from 'hooks/use-ui';
import BrandsFilter from './brands-filter';
import { CategorySubcategoryFilter } from './category-subcategory-filter';
import { EffectsFilter } from './effects-filter';
import PotencyFilter from './potency-filter';
import { SpecialsFilter } from './specials-filter';
import StrainTypeFilter from './strain-type-filter';
import SubcategoryFilter from './subcategory-filter';
import { SubcategoriesFilterMultiSelect } from './subcategory-filter/subcategory-filter-multi-select';
import { TerpenesFilter } from './terpenes-filter';
import { useFilterSidePanel } from './use-filter-side-panel';
import { useFilterVisibility } from './use-filter-visibility';
import WeightFilter from './weight-filter';
import type { MenuFilterOptions, SidebarCategory, SidebarDispensary } from './types';

type SidebarProps = {
  menuFilterOptions: MenuFilterOptions;
  category?: SidebarCategory | null;
  isLoading: boolean;
  route?: string;
  dispensary?: SidebarDispensary | null;
  onSearchPage?: boolean;
  onCollectionsPage?: boolean;
  customCategory?: unknown;
};

export default function Sidebar({
  menuFilterOptions,
  category,
  isLoading,
  route,
  dispensary,
  onSearchPage,
  onCollectionsPage,
  customCategory,
}: SidebarProps): JSX.Element {
  const UI = useUI();
  const isStoreFront = useObserver(() => UI.isStoreFront);
  const { sidebarRef, sidebarStyle } = useStickySidebar({ disableSticky: !isStoreFront });

  const visibility = useFilterVisibility({
    category,
    customCategory,
    dispensary,
    menuFilterOptions,
    onCollectionsPage,
    onSearchPage,
    route,
  });

  const panelContext = useMemo(
    () => ({
      onBrandsPage: visibility.onBrandsPage,
      onCollectionsPage: Boolean(onCollectionsPage),
      onSearchPage: Boolean(onSearchPage),
      route: route ?? '',
    }),
    [onCollectionsPage, onSearchPage, route, visibility.onBrandsPage]
  );

  const { CustomSidePanel, filterSidePanelProps } = useFilterSidePanel({
    category,
    context: panelContext,
    isLoading,
    menuFilterOptions,
    visibility,
  });

  const { brands, categoriesSubcategories, subcategories, strainTypes, weights, effects, terpenes } = menuFilterOptions;

  return (
    <TwoColumnSidebar ref={sidebarRef} $stickyStyle={sidebarStyle}>
      <EmptyLoadingState isLoading={isLoading} page='filterSideBar' delay={500}>
        {CustomSidePanel && filterSidePanelProps ? (
          <CustomSidePanel {...filterSidePanelProps} />
        ) : (
          <>
            {!customCategory && (
              <>
                {visibility.showCategorySubcategoryFilter && (
                  <CategorySubcategoryFilter categoriesSubcategories={categoriesSubcategories ?? []} />
                )}
                {visibility.showSubcategoryFilter &&
                  (visibility.showSubcategoriesFilterMultiSelect ? (
                    <SubcategoriesFilterMultiSelect subcategories={subcategories ?? []} />
                  ) : (
                    <SubcategoryFilter category={category} subcategories={subcategories ?? []} />
                  ))}
              </>
            )}
            {visibility.showSpecialsFilter && <SpecialsFilter />}
            {visibility.showWeights && <WeightFilter weights={weights ?? []} />}
            {visibility.showBrands && <BrandsFilter brands={brands ?? []} />}
            {visibility.showStrainTypes && <StrainTypeFilter strainTypes={strainTypes ?? []} />}
            {visibility.showPotencies && <PotencyFilter category={category} />}
            {visibility.showEffects && <EffectsFilter effects={effects ?? []} />}
            {visibility.showTerpenes && <TerpenesFilter terpenes={terpenes ?? []} />}
          </>
        )}
      </EmptyLoadingState>
    </TwoColumnSidebar>
  );
}
